/**
 * Disbursement request workflow (NOS-171): child-side create/cancel,
 * sponsor-side decide. All transitions run through RPCs; notifications are
 * fire-and-forget via the notify-disbursement function.
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"

export interface DisbursementRequest {
  id: string
  sponsorship_id: string
  child_org_id: string
  amount: number
  purpose: string
  status: "pending" | "approved" | "denied" | "cancelled"
  requested_by: string
  decided_by: string | null
  decision_note: string | null
  created_at: string
  decided_at: string | null
}

async function notify(requestId: string, event: "submitted" | "decided"): Promise<void> {
  try {
    const { data: sess } = await supabase.auth.getSession()
    await supabase.functions.invoke("notify-disbursement", {
      body: { request_id: requestId, event },
      ...(sess.session?.access_token
        ? { headers: { Authorization: `Bearer ${sess.session.access_token}` } }
        : {}),
    })
  } catch (err) {
    console.error("Disbursement notification failed (non-fatal):", err)
  }
}

export interface DisbursementRequests {
  loading: boolean
  requests: DisbursementRequest[]
  refresh: () => void
  submit: (input: {
    sponsorshipId: string
    childOrgId: string
    amountCents: number
    purpose: string
  }) => Promise<{ error: Error | null }>
  cancel: (requestId: string) => Promise<{ error: Error | null }>
  decide: (requestId: string, approve: boolean, note?: string) => Promise<{ error: Error | null }>
}

export function useDisbursementRequests(
  filter: { childOrgId?: string; sponsorshipId?: string } | null
): DisbursementRequests {
  const [loading, setLoading] = useState(true)
  const [requests, setRequests] = useState<DisbursementRequest[]>([])
  const childOrgId = filter?.childOrgId
  const sponsorshipId = filter?.sponsorshipId

  const fetchRequests = useCallback(async () => {
    if (!childOrgId && !sponsorshipId) {
      setRequests([])
      setLoading(false)
      return
    }
    setLoading(true)
    let query = supabase
      .from("disbursement_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200)
    if (childOrgId) query = query.eq("child_org_id", childOrgId)
    if (sponsorshipId) query = query.eq("sponsorship_id", sponsorshipId)
    const { data, error } = await query
    if (error) {
      console.error("Disbursement requests fetch error:", error)
      setRequests([])
    } else {
      setRequests((data ?? []) as DisbursementRequest[])
    }
    setLoading(false)
  }, [childOrgId, sponsorshipId])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const submit = useCallback(
    async (input: {
      sponsorshipId: string
      childOrgId: string
      amountCents: number
      purpose: string
    }) => {
      const { data: userData } = await supabase.auth.getUser()
      if (!userData?.user) return { error: new Error("Not authenticated") }
      const { data, error } = await supabase
        .from("disbursement_requests")
        .insert({
          sponsorship_id: input.sponsorshipId,
          child_org_id: input.childOrgId,
          amount: input.amountCents,
          purpose: input.purpose.trim(),
          requested_by: userData.user.id,
        })
        .select("id")
        .single()
      if (error) return { error: new Error(error.message) }
      if (data?.id) void notify(data.id, "submitted")
      await fetchRequests()
      return { error: null }
    },
    [fetchRequests]
  )

  const cancel = useCallback(
    async (requestId: string) => {
      const { error } = await supabase.rpc("cancel_disbursement_request", {
        p_request_id: requestId,
      })
      if (error) return { error: new Error(error.message) }
      await fetchRequests()
      return { error: null }
    },
    [fetchRequests]
  )

  const decide = useCallback(
    async (requestId: string, approve: boolean, note?: string) => {
      const { error } = await supabase.rpc("decide_disbursement_request", {
        p_request_id: requestId,
        p_approve: approve,
        p_note: note ?? undefined,
      })
      if (error) return { error: new Error(error.message) }
      void notify(requestId, "decided")
      await fetchRequests()
      return { error: null }
    },
    [fetchRequests]
  )

  return { loading, requests, refresh: fetchRequests, submit, cancel, decide }
}
